import React, { cloneElement, memo, ReactElement } from 'react';

import { FileRejection, useDropzone } from 'react-dropzone';

import { getFileExtension, isEncryptedPdf } from 'common/files/files.utils';
import { notify } from 'components/common/Toast/toast.helpers';

import {
  ALLOWED_EXTENSIONS,
  DEFAULT_MAX_FILE_SIZE,
  FILE_NUMBER_LIMIT,
  FILE_TOO_LARGE_ERROR_CODE,
  INVALID_TYPE_ERROR_CODE,
} from './dropzone.constants';
import { UploadFailureNotification } from './dropzone.enums';

export interface DropzoneProps {
  extensions?: Record<string, string[]>;
  onHandleFiles: (files: File[]) => void;
  disabled?: boolean;
  children: ReactElement;
}

const Dropzone = memo(({ extensions = ALLOWED_EXTENSIONS, disabled, onHandleFiles, children }: DropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: extensions,
    multiple: true,
    maxSize: DEFAULT_MAX_FILE_SIZE,
    noKeyboard: true,
    disabled: disabled,
    onDrop: async (accepted: File[], rejected: FileRejection[]) => {
      if (accepted.length + rejected.length > FILE_NUMBER_LIMIT) {
        notify(UploadFailureNotification.FileLimitError, 'error');

        return;
      }

      if (rejected.length) {
        switch (rejected[0].errors[0].code) {
          case FILE_TOO_LARGE_ERROR_CODE:
            notify(UploadFailureNotification.LargeDocument, 'error');

            return;

          case INVALID_TYPE_ERROR_CODE:
            const fileName = rejected[0].file.name;
            const extension = getFileExtension(fileName);

            notify(`File Extension Not Supported: .${extension}`, 'error');

            return;
          default:
            notify(UploadFailureNotification.DocumentNotUploaded, 'error');

            return;
        }
      }

      for (const initialFile of accepted) {
        const isEncrypted = await isEncryptedPdf(initialFile);

        if (isEncrypted) {
          notify(UploadFailureNotification.EncryptedDocument, 'error');

          return;
        }
      }

      onHandleFiles(accepted);
    },
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {cloneElement(children, {
        isDragActive: isDragActive,
      })}
    </div>
  );
});

export default Dropzone;
